import type { MediaDevice } from '../../types';

/**
 * Gets all available media devices.
 */
export async function getMediaDevices(): Promise<MediaDevice[]> {
  const devices = await navigator.mediaDevices.enumerateDevices();

  return devices
    .filter((device) => device.deviceId !== '')
    .map((device) => ({
      deviceId: device.deviceId,
      label: device.label || `${device.kind} (${device.deviceId.slice(0, 8)})`,
      kind: device.kind as MediaDevice['kind'],
    }));
}

/**
 * Gets available microphones.
 */
export async function getMicrophones(): Promise<MediaDevice[]> {
  const devices = await getMediaDevices();
  return devices.filter((d) => d.kind === 'audioinput');
}

/**
 * Gets available cameras.
 */
export async function getCameras(): Promise<MediaDevice[]> {
  const devices = await getMediaDevices();
  return devices.filter((d) => d.kind === 'videoinput');
}

/**
 * Gets available speakers.
 */
export async function getSpeakers(): Promise<MediaDevice[]> {
  const devices = await getMediaDevices();
  return devices.filter((d) => d.kind === 'audiooutput');
}

export interface MediaPermissionResult {
  audio: boolean;
  video: boolean;
  hasAudioDevice: boolean;
  hasVideoDevice: boolean;
}

/**
 * Checks which media device types are available on this machine.
 */
export async function getAvailableDeviceTypes(): Promise<{
  hasAudio: boolean;
  hasVideo: boolean;
}> {
  const devices = await navigator.mediaDevices.enumerateDevices();
  return {
    hasAudio: devices.some((d) => d.kind === 'audioinput'),
    hasVideo: devices.some((d) => d.kind === 'videoinput'),
  };
}

/**
 * Requests media permissions for available devices.
 * Handles cases where audio or video devices may not exist.
 */
export async function requestMediaPermissions(): Promise<boolean> {
  const result = await requestMediaPermissionsDetailed();
  return result.audio || result.video;
}

/**
 * Requests media permissions with detailed results per device type.
 * Only requests permissions for device types that exist on the machine.
 */
export async function requestMediaPermissionsDetailed(): Promise<MediaPermissionResult> {
  const { hasAudio, hasVideo } = await getAvailableDeviceTypes();

  const result: MediaPermissionResult = {
    audio: false,
    video: false,
    hasAudioDevice: hasAudio,
    hasVideoDevice: hasVideo,
  };

  if (!hasAudio && !hasVideo) {
    return result;
  }

  if (hasAudio && hasVideo) {
    const bothGranted = await tryGetUserMedia({ audio: true, video: { facingMode: 'user' } });
    if (bothGranted) {
      result.audio = true;
      result.video = true;
      return result;
    }
    result.audio = await tryGetUserMedia({ audio: true });
    result.video = await tryGetUserMedia({ video: { facingMode: 'user' } });
    return result;
  }

  if (hasAudio) {
    result.audio = await tryGetUserMedia({ audio: true });
    return result;
  }

  result.video = await tryGetUserMedia({ video: { facingMode: 'user' } });
  return result;
}

async function tryGetUserMedia(constraints: MediaStreamConstraints): Promise<boolean> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    stream.getTracks().forEach((track) => track.stop());
    return true;
  } catch {
    return false;
  }
}

/**
 * Gets a media stream with specified devices.
 * Defaults to front-facing camera on mobile if no device specified.
 */
export async function getMediaStream(
  audioDeviceId?: string,
  videoDeviceId?: string
): Promise<MediaStream> {
  const constraints: MediaStreamConstraints = {
    audio: audioDeviceId ? { deviceId: { exact: audioDeviceId } } : true,
    video: videoDeviceId
      ? { deviceId: { exact: videoDeviceId } }
      : { facingMode: 'user' },
  };

  return navigator.mediaDevices.getUserMedia(constraints);
}

/**
 * Gets a screen share stream.
 */
export async function getScreenShareStream(): Promise<MediaStream> {
  return navigator.mediaDevices.getDisplayMedia({
    video: true,
    audio: true,
  });
}

/**
 * Checks if the device is mobile.
 */
export function isMobileDevice(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

/**
 * Gets the front-facing camera device ID if available.
 */
export async function getFrontCameraId(): Promise<string | null> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user' },
    });
    const videoTrack = stream.getVideoTracks()[0];
    const deviceId = videoTrack?.getSettings().deviceId || null;
    stream.getTracks().forEach((track) => track.stop());
    return deviceId;
  } catch {
    return null;
  }
}

/**
 * Gets the back-facing camera device ID if available.
 */
export async function getBackCameraId(): Promise<string | null> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment' },
    });
    const videoTrack = stream.getVideoTracks()[0];
    const deviceId = videoTrack?.getSettings().deviceId || null;
    stream.getTracks().forEach((track) => track.stop());
    return deviceId;
  } catch {
    return null;
  }
}
